// src/app/router.ts — Router hash-based minimalista (compatible Pages, sin SSR)
export type Route = 'loading' | 'title' | 'newgame' | 'continue' | 'options' | 'world';

type RouteListener = (route: Route) => void;

const VALID: Set<string> = new Set(['loading', 'title', 'newgame', 'continue', 'options', 'world']);

class Router {
  private current: Route = 'loading';
  private listeners = new Set<RouteListener>();

  init(): void {
    window.addEventListener('hashchange', () => this.resolve());
    this.resolve();
  }

  navigate(route: Route): void {
    if (route === 'title') history.replaceState(null, '', '#/title');
    else location.hash = `#/${route}`;
    // resolve se dispara por hashchange; forzar si es el mismo hash
    this.resolve();
  }

  get(): Route {
    return this.current;
  }

  subscribe(fn: RouteListener): () => void {
    this.listeners.add(fn);
    fn(this.current);
    return () => this.listeners.delete(fn);
  }

  private resolve(): void {
    const raw = location.hash.replace(/^#\/?/, '').trim().toLowerCase();
    const normalized = raw === '' ? 'title' : raw;
    const target = (VALID.has(normalized) ? normalized : 'title') as Route;
    // Si estamos en loading, no forzar navegación externa hasta que el loader termine
    if (this.current === 'loading' && target !== 'loading') {
      // permitir salida de loading solo vía navigate() explícito
      // pero si el usuario entra directo con #/options, respetarlo tras el load
      // -> el caller (main) controlará la transición
    }
    const next = target;
    if (next !== this.current) {
      this.current = next as Route;
      for (const fn of this.listeners) fn(this.current);
    } else if (raw === '' && this.current !== 'title' && this.current !== 'loading') {
      this.current = 'title';
      for (const fn of this.listeners) fn(this.current);
    }
  }

  // Llamado por LoadingScreen al terminar
  finishLoading(): void {
    const raw = location.hash.replace(/^#\/?/, '').trim().toLowerCase();
    const target = VALID.has(raw) && raw !== 'loading' ? (raw as Route) : 'title';
    this.current = target;
    history.replaceState(null, '', `#/${target}`);
    for (const fn of this.listeners) fn(this.current);
  }
}

export const router = new Router();
